import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Stat = ({ label, value, testid }) => (
  <div className="border border-white/15 p-6" data-testid={testid}>
    <p className="text-[9px] tracking-[0.35em] uppercase text-white/40">{label}</p>
    <p className="font-serif text-4xl md:text-5xl mt-3 tabular-nums">{value}</p>
  </div>
);

export default function Studio() {
  const [key, setKey] = useState(() => sessionStorage.getItem("paeon_studio_key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tracking, setTracking] = useState({});

  const load = useCallback(
    async (k) => {
      try {
        const res = await api.get("/admin/dashboard", { headers: { "X-Admin-Token": k } });
        setData(res.data);
        setError("");
        setUpdatedAt(new Date());
      } catch {
        setError("Wrong key.");
        setData(null);
        sessionStorage.removeItem("paeon_studio_key");
        setKey("");
      }
    },
    []
  );

  useEffect(() => {
    if (!key) return;
    load(key);
    const t = setInterval(() => load(key), 30000);
    return () => clearInterval(t);
  }, [key, load]);

  const setOutOfStock = async (lookupKey, size) => {
    try {
      await api.post(
        "/admin/set-stock",
        { lookup_key: lookupKey, size, stock: 0 },
        { headers: { "X-Admin-Token": key } }
      );
      toast.success(`${size} marked out of stock`);
      load(key);
    } catch {
      toast.error("Could not update stock.");
    }
  };

  const restock = async (lookupKey, size) => {
    try {
      const res = await api.post(
        "/admin/restock",
        { lookup_key: lookupKey, size, quantity: 1 },
        { headers: { "X-Admin-Token": key } }
      );
      const n = res.data.notified || 0;
      toast.success(`+1 ${size} restocked${n ? ` — ${n} waitlist email${n > 1 ? "s" : ""} sent` : ""}`);
      load(key);
    } catch {
      toast.error("Restock failed.");
    }
  };

  const markShipped = async (sessionId) => {
    try {
      const res = await api.post(
        "/admin/mark-shipped",
        { session_id: sessionId, tracking: tracking[sessionId] || "" },
        { headers: { "X-Admin-Token": key } }
      );
      toast.success(
        res.data.email === "sent"
          ? "Marked shipped — buyer emailed"
          : res.data.email === "no_email_on_order"
            ? "Marked shipped (no email on this order)"
            : "Already shipped"
      );
      load(key);
    } catch {
      toast.error("Could not mark shipped.");
    }
  };

  const sendSummary = async () => {
    try {
      const res = await api.post("/admin/drop-summary", {}, { headers: { "X-Admin-Token": key } });
      toast.success(res.data.status === "sent" ? "Night report sent to your inbox" : "Report ready (no owner email set)");
    } catch {
      toast.error("Could not send summary.");
    }
  };

  if (!key) {
    return (
      <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <p className="font-serif text-3xl tracking-tight mb-2">PAEON STUDIO</p>
          <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-10">Owner access only</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input) {
                sessionStorage.setItem("paeon_studio_key", input);
                setKey(input);
              }
            }}
            placeholder="STUDIO KEY"
            data-testid="studio-key-input"
            className="w-full bg-transparent border border-white/25 px-4 py-4 text-xs tracking-[0.3em] uppercase text-center outline-none placeholder:text-white/25 focus:border-white"
          />
          <button
            onClick={() => {
              if (!input) return;
              sessionStorage.setItem("paeon_studio_key", input);
              setKey(input);
            }}
            data-testid="studio-enter-button"
            className="w-full mt-4 py-4 bg-white text-black text-xs tracking-[0.3em] uppercase font-semibold hover:bg-transparent hover:text-white border border-white transition-colors duration-300"
          >
            Enter
          </button>
          {error && (
            <p data-testid="studio-error" className="mt-4 text-[10px] tracking-[0.3em] uppercase text-white/50">
              {error}
            </p>
          )}
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 animate-pulse">Loading studio…</p>
      </main>
    );
  }

  const { stats, stock, orders, waitlist, mode } = data;
  const SIZES = ["S", "M", "L", "XL"];

  return (
    <main className="min-h-screen bg-[#050505] text-white px-6 md:px-12 py-12" data-testid="studio-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between border-b border-white/15 pb-8 mb-10">
          <div>
            <p className="font-serif text-4xl md:text-5xl tracking-tight">PAEON STUDIO</p>
            <p className="text-[9px] tracking-[0.35em] uppercase text-white/40 mt-2">
              Drop 001 — live operations · mode: {mode}
            </p>
          </div>
          <div className="text-right">
            <div className="flex gap-3 justify-end">
              <button
                onClick={sendSummary}
                data-testid="email-summary-button"
                className="border border-white/30 px-5 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
              >
                Email Drop Summary
              </button>
              <a
                href="/studio/assets"
                data-testid="media-kit-link"
                className="border border-white/30 px-5 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
              >
                Media Kit
              </a>
            </div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/30 mt-3" data-testid="studio-updated">
              {updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : ""} · auto 30s
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          <Stat label="Revenue" value={`$${stats.revenue_usd.toLocaleString()}`} testid="stat-revenue" />
          <Stat label="Orders" value={stats.orders} testid="stat-orders" />
          <Stat label="Units Sold" value={stats.units} testid="stat-units" />
          <Stat label="Waitlist" value={stats.waitlist_total} testid="stat-waitlist" />
        </div>

        <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-5">Stock</p>
        <div className="border border-white/15 mb-14" data-testid="stock-table">
          {stock.map((p) => (
            <div
              key={p.lookup_key}
              className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10 last:border-b-0"
              data-testid={`stock-row-${p.lookup_key}`}
            >
              <p className="text-[11px] tracking-[0.15em] uppercase flex-1 min-w-0 truncate pr-2">{p.name}</p>
              <div className="flex gap-2 md:gap-5 shrink-0">
                {SIZES.map((s) => {
                  const n = (p.stock || {})[s] ?? 0;
                  return (
                    <span key={s} className="text-center w-10">
                      <span className="block text-[8px] tracking-[0.25em] text-white/30">{s}</span>
                      <span
                        className={`block font-serif text-xl tabular-nums ${
                          n === 0 ? "line-through text-white/25" : n <= 2 ? "text-white font-semibold" : "text-white/70"
                        }`}
                      >
                        {n}
                      </span>
                      <button
                        onClick={() => restock(p.lookup_key, s)}
                        data-testid={`restock-${p.lookup_key}-${s}`}
                        title={`Restock ${s} +1 (emails waitlist)`}
                        className="mt-1 w-6 h-6 mx-auto block border border-white/20 text-[10px] text-white/50 hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
                      >
                        +
                      </button>
                      {n > 0 && (
                        <button
                          onClick={() => setOutOfStock(p.lookup_key, s)}
                          data-testid={`soldout-${p.lookup_key}-${s}`}
                          title={`Mark ${s} out of stock (sets to 0)`}
                          className="mt-1 w-6 h-6 mx-auto block border border-white/20 text-[10px] text-white/50 hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
                        >
                          0
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
              <p className="hidden md:block text-[10px] tracking-[0.25em] uppercase text-white/40 w-20 text-right">
                {p.total} left
              </p>
            </div>
          ))}
        </div>

        <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-5">Recent Orders</p>
        <div className="border border-white/15 mb-14" data-testid="orders-list">
          {orders.length === 0 && (
            <p className="px-5 py-8 text-[10px] tracking-[0.3em] uppercase text-white/30">No orders yet</p>
          )}
          {orders.map((o, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10 last:border-b-0"
            >
              <div>
                <p className="text-[11px] tracking-[0.1em] uppercase">
                  {o.items.map((it) => `${it.name.split("/").pop()?.trim()} · ${it.size} ×${it.quantity}`).join(" + ")}
                </p>
                <p className="text-[9px] tracking-[0.25em] uppercase text-white/35 mt-1.5">
                  {o.email} · {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                </p>
                {o.shipping_name && (
                  <p className="text-[9px] tracking-[0.15em] uppercase text-white/50 mt-1.5" data-testid={`order-shipping-${o.session_id}`}>
                    Ship to: {o.shipping_name}
                    {o.shipping_address?.line1 ? ` — ${o.shipping_address.line1}${o.shipping_address.line2 ? ", " + o.shipping_address.line2 : ""}, ${o.shipping_address.city}, ${o.shipping_address.state} ${o.shipping_address.postal_code}` : ""}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4">
                {o.status === "paid" && !o.shipped && (
                  <>
                    <input
                      value={tracking[o.session_id] || ""}
                      onChange={(e) => setTracking((t) => ({ ...t, [o.session_id]: e.target.value }))}
                      placeholder="TRACKING #"
                      data-testid={`tracking-input-${o.session_id}`}
                      className="hidden md:block bg-transparent border border-white/20 px-3 py-2 text-[9px] tracking-[0.2em] uppercase w-36 placeholder:text-white/25 focus:outline-none focus:border-white/50"
                    />
                    <button
                      onClick={() => markShipped(o.session_id)}
                      data-testid={`ship-order-${o.session_id}`}
                      className="border border-white/30 px-4 py-2 text-[9px] tracking-[0.3em] uppercase hover:bg-white hover:text-black hover:border-white transition-colors duration-200"
                    >
                      Mark Shipped
                    </button>
                  </>
                )}
                {o.status === "paid" && o.shipped && (
                  <span className="text-[9px] tracking-[0.3em] uppercase text-white/40 border border-white/15 px-4 py-2">
                    Shipped{o.tracking ? ` · ${o.tracking}` : ""}
                  </span>
                )}
                <p className={`text-sm font-medium tabular-nums ${o.status === "refunded" ? "line-through text-white/30" : ""}`}>
                  ${o.total_usd.toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-5">Restock Waitlist</p>
        <div className="border border-white/15" data-testid="waitlist-list">
          {waitlist.length === 0 && (
            <p className="px-5 py-8 text-[10px] tracking-[0.3em] uppercase text-white/30">No one waiting yet</p>
          )}
          {waitlist.map((w) => (
            <div key={w.item} className="px-5 py-4 border-b border-white/10 last:border-b-0">
              <div className="flex justify-between items-center">
                <p className="text-[11px] tracking-[0.15em] uppercase">{w.item}</p>
                <p className="font-serif text-xl tabular-nums">{w.count}</p>
              </div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-white/35 mt-1.5">{w.emails.join("  ·  ")}</p>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
